// src/pages/Courses.jsx - Complete refactored code with radio buttons
import React, { useEffect, useState, useMemo } from 'react';
import { Container, Alert, Spinner, Pagination, Collapse, Button } from 'react-bootstrap';
import { FaBook, FaGraduationCap, FaCode, FaFileAlt, FaClock, FaCalendarAlt, FaUser, FaList, FaSync, FaDownload, FaChevronUp, FaPlus } from 'react-icons/fa';
import axios from '../api/axios';
import Cookies from 'js-cookie';
import { useCrudOperations } from '../hooks/useCrudOperations';
import { useTableOperations } from '../hooks/useTableOperations';
import GenericForm from '../components/GenericForm';
import GenericTable from '../components/GenericTable';
import SearchAndFilters from '../components/SearchAndFilters';
import GenericViewModal from '../components/GenericViewModal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import { exportToCSV } from '../utils/csvExport';
import {
  usePrograms,
  useProgramYears,
  useAcademicYears,
  useCreditHours,
  useSemesters,
  useDisciplines,
  useDegrees,
} from '../hooks/useDropdownData';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const CoursesPage = () => {
  const [eduCourseTypes, setEduCourseTypes] = useState([]);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const token = Cookies.get('token');
  
  // Fix: Correct destructuring to match what hooks return
  const { programmes: programs, loading: programsLoading } = usePrograms();
  const { degrees, loading: degreesLoading } = useDegrees();
  const { programmeYears: programYears, loading: programYearsLoading } = useProgramYears();
  const { academicYears, loading: academicYearsLoading } = useAcademicYears();
  const { creditHours, loading: creditHoursLoading } = useCreditHours();
  const { semesters, loading: semestersLoading } = useSemesters();
  const { disciplines, loading: disciplinesLoading } = useDisciplines();

  // Configuration for the generic CRUD
  const config = {
    baseUrl: BASE_URL,
    endpoints: {
      fetch: '/master/getCourses',
      save: '/master/saveCourses',
      update: '/master/updateCourses',
      delete: '/master/deleteCourses'
    },
    validateForm: (form) => {
      const errors = {};
      if (!form.exName.trim()) errors.exName = 'Course name is required';
      if (!form.exCoursesCode.trim()) errors.exCoursesCode = 'Course code is required';
      if (!form.degree) errors.degree = 'Degree is required';
      if (!form.programmes) errors.programmes = 'Program is required';
      if (form.isElective === '') errors.isElective = 'Please select if course is elective';
      if (form.exIsPractical === '') errors.exIsPractical = 'Please select if course is practical';
      return errors;
    },
    initialFormState: {
      exName: '',
      exCoursesCode: '',
      exDescription: '',
      exCriteria: 1,
      isElective: '0', // Default to 'No'
      exIsPractical: '1', // Default to 'Yes'
      creditHours: '',
      academicYear: '',
      degree: '',
      programmes: '',
      programmeyear: '',
      discipline: '',
      semester: '',
      eduCourseType: '',
      exScheme: '',
      exIsActive: 1,
      user: 1,
    },
    entityName: 'Course'
  };

  const crud = useCrudOperations(config);
  const table = useTableOperations(crud.items, 'exName');

  // Debug effect to monitor disciplines data
  useEffect(() => {
    console.log('Disciplines data changed:', {
      disciplines,
      disciplinesLoading,
      length: disciplines ? disciplines.length : 0,
      rawData: disciplines
    });
    
    // Force re-fetch disciplines if empty and not loading
    if (!disciplinesLoading && (!disciplines || disciplines.length === 0)) {
      console.log('Disciplines is empty, this might indicate an API issue');
      
      // Manual fetch to debug
      const debugFetchDisciplines = async () => {
        try {
          const token = Cookies.get('token');
          const response = await axios.get(`${BASE_URL}/master/getDiscipline`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          console.log('Manual discipline fetch response:', response.data);
        } catch (error) {
          console.error('Manual discipline fetch failed:', error);
        }
      };
      
      debugFetchDisciplines();
    }
  }, [disciplines, disciplinesLoading]);

  // Fetch EduCourseTypes when degree changes in form
  useEffect(() => {
    const fetchEduCourseTypes = async () => {
      if (!crud.form.degree) {
        console.log('No degree selected, clearing eduCourseTypes');
        setEduCourseTypes([]);
        return;
      }

      console.log('Fetching eduCourseTypes for degree:', crud.form.degree);
      try {
        const res = await axios.post(
          `${BASE_URL}/masters/getEduCoursesType`,
          { degree: parseInt(crud.form.degree), user: 1 },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );
        const data = res.data?.details || [];
        console.log('Received eduCourseTypes data:', data);
        const formatted = data.map((item) => ({
          id: item.id,
          exName: item.exEduCourseTypeName,
        }));
        console.log('Formatted eduCourseTypes:', formatted);
        setEduCourseTypes(formatted);
      } catch (err) {
        console.error('Failed to fetch EduCourseTypes', err);
        setEduCourseTypes([]);
      }
    };

    fetchEduCourseTypes();
  }, [crud.form.degree, token]);

  // Helper function to create dropdown options
  const createDropdownOptions = (array, loading, placeholder, labelKey = 'exName', valueKey = 'id') => {
    if (loading) {
      return [{ value: '', label: `Loading...` }];
    }
    
    if (!array || array.length === 0) {
      return [{ value: '', label: `No ${placeholder} available` }];
    }
    
    // Just return the mapped options without extra "Select" option
    return array.map(item => ({
      value: item[valueKey] || item.id,
      label: item[labelKey] || item.exName || item.name || `${placeholder} ${item.id}`
    }));
  };

  // Dynamic form fields that update based on state
  const formFields = useMemo(() => {
    console.log('Form fields memo - Programs:', programs);
    console.log('Form fields memo - Degrees:', degrees);
    console.log('Form fields memo - Disciplines:', disciplines);
    console.log('Disciplines loading:', disciplinesLoading);
    
    // Special handling for disciplines since it might have different structure
    const getDisciplineOptions = () => {
      console.log('getDisciplineOptions called with:', { disciplines, disciplinesLoading });
      
      if (disciplinesLoading) {
        return [{ value: '', label: 'Loading disciplines...' }];
      }
      
      // Handle different possible data structures
      let disciplineArray = disciplines;
      
      // If disciplines is an object with a nested array
      if (disciplines && typeof disciplines === 'object' && !Array.isArray(disciplines)) {
        console.log('Disciplines is object, looking for array inside');
        disciplineArray = disciplines.disciplines || disciplines.discipline || disciplines.data || [];
      }
      
      // If it's still not an array
      if (!Array.isArray(disciplineArray)) {
        console.log('Disciplines is not an array:', disciplineArray);
        return [{ value: '', label: 'Invalid disciplines data' }];
      }
      
      if (disciplineArray.length === 0) {
        return [{ value: '', label: 'No disciplines found' }];
      }
      
      console.log('Processing disciplines array:', disciplineArray);
      const options = disciplineArray.map((item, index) => {
        console.log(`Discipline item ${index}:`, item);
        
        // Handle different possible field names
        const id = item.id || item.disciplineId || item.value || index;
        const name = item.exName || item.name || item.disciplineName || item.label || `Discipline ${id}`;
        
        return {
          value: id,
          label: name
        };
      });
      
      console.log('Final discipline options:', options);
      return options;
    };
    
    return [
      {
        name: 'exName',
        label: 'Course Name',
        icon: FaBook,
        required: true,
        type: 'text'
      },
      {
        name: 'exCoursesCode',
        label: 'Course Code',
        icon: FaCode,
        required: true,
        type: 'text'
      },
      {
        name: 'exDescription',
        label: 'Description',
        icon: FaFileAlt,
        required: false,
        as: 'textarea',
        rows: 3
      },
      {
        name: 'degree',
        label: 'Degree',
        icon: FaGraduationCap,
        required: true,
        as: 'select',
        disabled: degreesLoading,
        options: createDropdownOptions(degrees, degreesLoading, 'Degree')
      },
      {
        name: 'programmes',
        label: 'Program',
        icon: FaList,
        required: true,
        as: 'select',
        disabled: programsLoading,
        options: createDropdownOptions(programs, programsLoading, 'Program')
      },
      {
        name: 'programmeyear',
        label: 'Program Year',
        icon: FaCalendarAlt,
        required: false,
        as: 'select',
        disabled: programYearsLoading,
        options: createDropdownOptions(programYears, programYearsLoading, 'Program Year')
      },
      {
        name: 'discipline',
        label: 'Discipline',
        icon: FaUser,
        required: false,
        as: 'select',
        disabled: disciplinesLoading,
        options: getDisciplineOptions()
      },
      {
        name: 'semester',
        label: 'Semester',
        icon: FaCalendarAlt,
        required: false,
        as: 'select',
        disabled: semestersLoading,
        options: createDropdownOptions(semesters, semestersLoading, 'Semester')
      },
      {
        name: 'academicYear',
        label: 'Academic Year',
        icon: FaCalendarAlt,
        required: false,
        as: 'select',
        disabled: academicYearsLoading,
        options: createDropdownOptions(academicYears, academicYearsLoading, 'Academic Year', 'exYear')
      },
      {
        name: 'creditHours',
        label: 'Credit Hours',
        icon: FaClock,
        required: false,
        as: 'select',
        disabled: creditHoursLoading,
        options: createDropdownOptions(creditHours, creditHoursLoading, 'Credit Hours', 'exCreditHourTitle')
      },
      {
        name: 'eduCourseType',
        label: 'Edu Course Type',
        icon: FaList,
        required: false,
        as: 'select',
        disabled: !crud.form.degree,
        options: !crud.form.degree ? [
          { value: '', label: 'Please select degree first' }
        ] : createDropdownOptions(eduCourseTypes, false, 'Edu Course Type')
      },
      {
        name: 'exScheme',
        label: 'Scheme',
        icon: FaList,
        required: false,
        type: 'text'
      },
 {
  name: 'isElective',
  label: 'Is Elective',
  icon: FaList,
  required: true,
  as: 'switch'
},
{
  name: 'exIsPractical',
  label: 'Is Practical',
  icon: FaList,
  required: true,
  as: 'switch'
}
    ];
  }, [
    degrees, degreesLoading,
    programs, programsLoading,
    programYears, programYearsLoading,
    disciplines, disciplinesLoading,
    semesters, semestersLoading,
    academicYears, academicYearsLoading,
    creditHours, creditHoursLoading,
    eduCourseTypes,
    crud.form.degree
  ]);

  // Transform payload for API
  const transformPayload = (form, editId, userId) => {
    return {
      ...form,
      degree: parseInt(form.degree) || 0,
      programmes: parseInt(form.programmes) || 0,
      programmeyear: parseInt(form.programmeyear) || 0,
      discipline: parseInt(form.discipline) || 0,
      semester: parseInt(form.semester) || 0,
      academicYear: parseInt(form.academicYear) || 0,
      creditHours: parseInt(form.creditHours) || 0,
      eduCourseType: parseInt(form.eduCourseType) || 0,
      isElective: parseInt(form.isElective),
      exIsPractical: parseInt(form.exIsPractical),
      exIsActive: parseInt(form.exIsActive),
      exCriteria: parseInt(form.exCriteria),
      exScheme: form.exScheme || '',
      user: userId,
      ...(editId && { id: editId })
    };
  };

  // Transform item data for form editing
  const transformToForm = (item) => {
    // Helper function to convert boolean/string/number to string for radio buttons
    const toStringValue = (value) => {
      if (value === true || value === 1 || value === '1') return '1';
      if (value === false || value === 0 || value === '0') return '0';
      return '0'; // default to 'No'
    };

    return {
      exName: item.exName || '',
      exCoursesCode: item.exCoursesCode || '',
      exDescription: item.exDescription || '',
      exCriteria: item.exCriteria ?? 1,
      isElective: toStringValue(item.isElective),
      exIsPractical: toStringValue(item.exIsPractical),
      creditHours: item.creditHoursId?.toString() || '',
      academicYear: item.exAcademicYearId?.toString() || '',
      degree: item.degreeId?.toString() || '',
      programmes: item.programmesId?.toString() || '',
      programmeyear: item.programmeyearId?.toString() || '',
      discipline: item.disciplineId?.toString() || '',
      semester: item.semesterId?.toString() || '',
      eduCourseType: item.eduCourseTypeId?.toString() || '',
      exScheme: item.exScheme || '',
      exIsActive: toStringValue(item.exIsActive),
      user: 1,
    };
  };

  // Custom form change handler to reset eduCourseType when degree changes
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    
    // Handle edu course type selection validation
    if (name === 'eduCourseType' && !crud.form.degree) {
      alert('Please select a degree first before choosing edu course type.');
      return;
    }
    
    // Reset eduCourseType when degree changes
    if (name === 'degree') {
      crud.handleChange({ target: { name: 'eduCourseType', value: '' } });
    }
    
    crud.handleChange(e);
  };

  const handleSubmit = async (e) => {
    const success = await crud.handleSubmit(e, transformPayload);
    if (success) setIsFormVisible(false);
  };

  const handleEdit = (item) => {
    crud.handleEdit(item, transformToForm);
    setIsFormVisible(true);
  };

  const handleCancelForm = () => {
    crud.resetForm();
    setIsFormVisible(false);
  };

  const handleExport = () => {
    const csvHeaders = [
      { key: 'exName', label: 'Course Name' },
      { key: 'exCoursesCode', label: 'Course Code' },
      { key: 'exDescription', label: 'Description' },
      { key: 'degreeName', label: 'Degree' },
      { key: 'programmeName', label: 'Program' },
      { key: 'eduCourseTypeName', label: 'Edu Course Type' },
      { key: 'status', label: 'Status' }
    ];

    // Helper function to find names from IDs
    const findNameById = (array, id, nameField = 'exName') => {
      if (!id || !array || !array.length) return null;
      const found = array.find(item => String(item.id) === String(id));
      return found ? found[nameField] : null;
    };

    const dataToExport = table.filteredAndSortedItems.map(item => {
      const eduCourseTypeName = item.eduCourseTypeName || findNameById(eduCourseTypes, item.eduCourseTypeId);

      return {
        ...item,
        degreeName: item.degreeName || findNameById(degrees, item.degreeId) || 'N/A',
        programmeName: item.programmeName || findNameById(programs, item.programmesId) || 'N/A',
        eduCourseTypeName: eduCourseTypeName || 'N/A',
        status: (item.exIsActive === 1 || item.exIsActive === true) ? 'Active' : 'Inactive'
      };
    });

    exportToCSV(dataToExport, csvHeaders, 'courses-export.csv');
  };

  // Get view fields for modal
  const getViewFields = (item) => {
    if (!item) return [];
    
    // Helper function to find name by ID
    const findNameById = (array, id, nameField = 'exName') => {
      if (!id || !array || array.length === 0) return '-';
      const found = array.find(item => item.id == id);
      return found ? found[nameField] : '-';
    };
    
    return [
      { label: 'Course Name', value: item.exName || '-', icon: FaBook },
      { label: 'Course Code', value: item.exCoursesCode || '-', icon: FaCode },
      { label: 'Description', value: item.exDescription || '-', icon: FaFileAlt, fullWidth: true },
      { 
        label: 'Degree', 
        value: item.degreeName || findNameById(degrees, item.degreeId), 
        icon: FaGraduationCap 
      },
      { 
        label: 'Program', 
        value: item.programmeName || findNameById(programs, item.programmesId), 
        icon: FaList 
      },
      { 
        label: 'Program Year', 
        value: item.programYearName || findNameById(programYears, item.programmeyearId), 
        icon: FaCalendarAlt 
      },
      { 
        label: 'Discipline', 
        value: item.disciplineName || findNameById(disciplines, item.disciplineId), 
        icon: FaUser 
      },
      { 
        label: 'Semester', 
        value: item.semesterName || findNameById(semesters, item.semesterId), 
        icon: FaCalendarAlt 
      },
      { 
        label: 'Academic Year', 
        value: item.academicYearName || findNameById(academicYears, item.exAcademicYearId, 'exYear'), 
        icon: FaCalendarAlt 
      },
      { 
        label: 'Credit Hours', 
        value: item.creditHoursName || findNameById(creditHours, item.creditHoursId, 'exCreditHourTitle'), 
        icon: FaClock 
      },
      { 
        label: 'Edu Course Type', 
        value: item.eduCourseTypeName || findNameById(eduCourseTypes, item.eduCourseTypeId), 
        icon: FaList 
      },
      { 
        label: 'Scheme', 
        value: item.exScheme || '-', 
        icon: FaList 
      },
      { 
        label: 'Is Elective', 
        value: (item.isElective === 1 || item.isElective === '1' || item.isElective === true) ? 'Yes' : 'No', 
        icon: FaList 
      },
      { 
        label: 'Is Practical', 
        value: (item.exIsPractical === 1 || item.exIsPractical === '1' || item.exIsPractical === true) ? 'Yes' : 'No', 
        icon: FaList 
      }
    ];
  };

  // Show loading if any critical dropdown data is still loading
  const isInitialLoading = degreesLoading || programsLoading;

  if (isInitialLoading) {
    return (
      <Container fluid className="py-4 px-lg-5 bg-light-subtle min-vh-100">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
          <p className="mt-3 text-muted">Loading configuration...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4 px-lg-5 bg-light-subtle min-vh-100">
      {crud.success && (
        <Alert
          variant="success"
          className="position-fixed top-0 end-0 m-3 shadow-lg"
          style={{ zIndex: 1056 }}
          dismissible
          onClose={() => crud.setSuccess('')}
        >
          {crud.success}
        </Alert>
      )}
      {crud.error && (
        <Alert
          variant="danger"
          className="position-fixed top-0 end-0 m-3 shadow-lg"
          style={{ zIndex: 1056 }}
          dismissible
          onClose={() => crud.setError('')}
        >
          {crud.error}
        </Alert>
      )}

      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fw-bolder page-title">Courses</h2>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={crud.fetchItems} disabled={crud.loading}>
            <FaSync className={crud.loading ? 'fa-spin' : ''} /> Refresh
          </Button>
          <Button variant="outline-success" onClick={handleExport}>
            <FaDownload /> Export CSV
          </Button>
          <Button
            variant="primary"
            className="btn-gradient"
            onClick={() => setIsFormVisible(!isFormVisible)}
          >
            {isFormVisible ? (
              <><FaChevronUp className="me-1" /> Hide Form</>
            ) : (
              <><FaPlus className="me-1" /> Add Course</>
            )}
          </Button>
        </div>
      </div>

      <Collapse in={isFormVisible}>
        <div>
          <GenericForm
            title="Course"
            onSubmit={handleSubmit}
            form={crud.form}
            handleChange={handleFormChange}
            formErrors={crud.formErrors}
            submitting={crud.submitting}
            editId={crud.editId}
            onCancel={handleCancelForm}
            fields={formFields}
          />
        </div>
      </Collapse>

      <SearchAndFilters
        search={table.search}
        setSearch={table.setSearch}
        statusFilter={table.statusFilter}
        setStatusFilter={table.setStatusFilter}
        itemsPerPage={table.itemsPerPage}
        setItemsPerPage={table.setItemsPerPage}
        startIndex={table.startIndex}
        totalItems={table.filteredAndSortedItems.length}
        searchPlaceholder="Search courses by name, code, description..."
      />

      {crud.loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
          <p className="mt-3 text-muted">Loading Courses...</p>
        </div>
      ) : (
        <>
          <GenericTable
            items={table.paginatedItems}
            columns={[
              { key: 'exName', label: 'Course Name', sortable: true },
              { key: 'exCoursesCode', label: 'Course Code', sortable: true },
              { 
                key: 'exDescription', 
                label: 'Description', 
                sortable: false, 
                render: (item) => item.exDescription ? (item.exDescription.length > 50 ? item.exDescription.substring(0, 50) + '...' : item.exDescription) : '-'
              },
              { 
                key: 'eduCourseTypeName', 
                label: 'Edu Course Type', 
                sortable: false, 
                render: (item) => {
                  if (item.eduCourseTypeName) return item.eduCourseTypeName;
                  if (item.eduCourseTypeId) {
                    const found = eduCourseTypes.find(ect => ect.id === item.eduCourseTypeId);
                    return found ? found.exName : '-';
                  }
                  return '-';
                }
              },
              { 
                key: 'degreeName', 
                label: 'Degree', 
                sortable: false, 
                render: (item) => {
                  if (item.degreeName) return item.degreeName;
                  if (item.degreeId) {
                    const found = (degrees || []).find(d => d.id === item.degreeId);
                    return found ? found.exName : '-';
                  }
                  return '-';
                }
              },
              { 
                key: 'programmeName', 
                label: 'Program', 
                sortable: false, 
                render: (item) => {
                  if (item.programmeName) return item.programmeName;
                  if (item.programmesId) {
                    const found = (programs || []).find(p => p.id === item.programmesId);
                    return found ? found.exName : '-';
                  }
                  return '-';
                }
              },
              { 
                key: 'isElective', 
                label: 'Elective', 
                sortable: false, 
                render: (item) => (item.isElective === 1 || item.isElective === '1' || item.isElective === true) ? 'Yes' : 'No'
              },
              { 
                key: 'exIsPractical', 
                label: 'Practical', 
                sortable: false, 
                render: (item) => (item.exIsPractical === 1 || item.exIsPractical === '1' || item.exIsPractical === true) ? 'Yes' : 'No'
              }
            ]}
            onSort={table.handleSort}
            onEdit={handleEdit}
            onView={(item) => { setSelectedItem(item); setShowViewModal(true); }}
            onDelete={(item) => { setSelectedItem(item); setShowDeleteModal(true); }}
            toggleStatus={crud.toggleStatus}
            sortField={table.sortField}
            sortDirection={table.sortDirection}
            emptyIcon={FaBook}
            emptyTitle="No Courses Found"
          />
          {table.totalPages > 1 && (
            <div className="d-flex justify-content-center mt-4">
              <Pagination>
                <Pagination.First
                  onClick={() => table.setCurrentPage(1)}
                  disabled={table.currentPage === 1}
                />
                <Pagination.Prev
                  onClick={() => table.setCurrentPage(c => Math.max(1, c - 1))}
                  disabled={table.currentPage === 1}
                />
                <Pagination.Item active>
                  {table.currentPage} of {table.totalPages}
                </Pagination.Item>
                <Pagination.Next
                  onClick={() => table.setCurrentPage(c => Math.min(table.totalPages, c + 1))}
                  disabled={table.currentPage === table.totalPages}
                />
                <Pagination.Last
                  onClick={() => table.setCurrentPage(table.totalPages)}
                  disabled={table.currentPage === table.totalPages}
                />
              </Pagination>
            </div>
          )}
        </>
      )}

      <GenericViewModal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        item={selectedItem}
        title="Course"
        fields={getViewFields(selectedItem)}
      />
      
      <DeleteConfirmationModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={() => crud.handleDelete(selectedItem).then(() => setShowDeleteModal(false))}
        itemName={selectedItem?.exName}
        submitting={crud.submitting}
      />
    </Container>
  );
};

export default CoursesPage;